/**
 * useResourceStore — Notes, links, files (PARA Resources).
 *
 * Storage: 'resources' (collection)
 * Entity: { id, title, summary, content, type('note'|'link'|'file'), areaId, projectId, tags[], status }
 * Methods: addResource, updateResource, deleteResource
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';

const STORAGE_KEY = 'resources';

const useResourceStore = create((set, get) => ({
    resources: storage.get(STORAGE_KEY, []),

    _persist() {
        storage.set(STORAGE_KEY, get().resources);
    },

    // ==================== CRUD ====================

    addResource(resource) {
        const now = new Date().toISOString();
        const newResource = {
            id: crypto.randomUUID(),
            title: resource.title || 'Untitled Resource',
            summary: resource.summary || '', // Layer 4: Executive Summary
            content: resource.content || '',
            type: resource.type || 'note', // 'note' | 'link' | 'file'
            areaId: resource.areaId || null,
            projectId: resource.projectId || null,
            tags: resource.tags || [],
            status: 'active', // 'active' | 'archived'
            createdAt: now,
            updatedAt: now,
        };
        set(state => ({ resources: [...state.resources, newResource] }));
        get()._persist();
        return newResource;
    },

    updateResource(id, updates) {
        set(state => ({
            resources: state.resources.map(r =>
                r.id === id
                    ? { ...r, ...updates, updatedAt: new Date().toISOString() }
                    : r
            ),
        }));
        get()._persist();
    },

    deleteResource(id) {
        set(state => ({
            resources: state.resources.filter(r => r.id !== id),
        }));
        get()._persist();
    },

    archiveResource(id) {
        get().updateResource(id, { status: 'archived' });
    },

    unarchiveResource(id) {
        get().updateResource(id, { status: 'active' });
    },

    // ==================== QUERIES ====================

    getActiveResources() {
        return get().resources.filter(r => r.status === 'active');
    },

    getArchivedResources() {
        return get().resources.filter(r => r.status === 'archived');
    },

    getResourcesByProject(projectId) {
        return get().resources.filter(r => r.projectId === projectId && r.status === 'active');
    },

    getResourcesByArea(areaId) {
        return get().resources.filter(r => r.areaId === areaId && r.status === 'active');
    },

    getResourcesByType(type) {
        return get().resources.filter(r => r.type === type && r.status === 'active');
    },
}));

// Re-read from localStorage after cloud sync completes
onRehydrate(() => {
    useResourceStore.setState({ resources: storage.get(STORAGE_KEY, []) });
});

export default useResourceStore;
